import { Fragment, useState } from 'react';
import { BsArrowDown, BsArrowRight, BsLightningChargeFill } from 'react-icons/bs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import { setRoutingFallbackMessage } from '../api/api';
import { showToast } from '../../../store/toastStore';
import type { RoutingRule, RoutingRuleGroup } from '../models/types';

/** The fallback shown (and sent to the acquirer-free denial) when a rule leaves its message blank. */
const DEFAULT_FALLBACK_DENIAL_MESSAGE = 'violation, this transaction cannot be completed.';

const compact = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });

function fmtList(value: string | null | undefined): string {
  const v = (value ?? '').trim();
  if (!v || v === '*') return 'Any';
  return v
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .join(', ');
}
function fmtOpList(value: string | null | undefined, operator: string | undefined): string {
  const list = fmtList(value);
  return operator === 'NOT_IN' && list !== 'Any' ? `NOT IN ${list}` : list;
}
function fmtRange(g: RoutingRuleGroup): string {
  return `${compact.format(g.minAmount ?? 0)} – ${compact.format(g.maxAmount ?? 0)}`;
}

function Chip({ label, tone }: { label: string; tone: 'country' | 'currency' | 'amount' | 'device' }) {
  const styles = {
    country: 'bg-primary/15 text-primary',
    currency: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
    amount: 'bg-secondary text-foreground/80 font-mono',
    device: 'bg-violet-500/15 text-violet-700 dark:text-violet-400',
  }[tone];
  return (
    <span
      className={`inline-block max-w-full whitespace-normal break-words rounded-md px-2 py-0.5 text-xs font-medium leading-relaxed ${styles}`}
    >
      {label}
    </span>
  );
}

/** A downward "no match" connector between decision nodes. */
function DownConnector({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center py-1" aria-hidden>
      <div className="h-4 w-px bg-border" />
      <span className="rounded-full border border-border bg-secondary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-foreground/70">
        {label}
      </span>
      <BsArrowDown className="mt-0.5 h-3 w-3 text-foreground/50" />
    </div>
  );
}

/**
 * Read-only visual "simulation" of a rule: a payment enters at the top and falls through the groups.
 * Each group branches RIGHT to its MID on a match, or DOWN to the next group on "no match", ending at
 * the global fallback (or a rejection).
 */
export default function RoutingRuleViewDialog({
  rule,
  onClose,
  onSaved,
}: {
  rule: RoutingRule;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const groups = rule.groups ?? [];
  const [denialMessage, setDenialMessage] = useState(
    rule.fallbackDenialMessage ?? DEFAULT_FALLBACK_DENIAL_MESSAGE,
  );
  const [savedBaseline, setSavedBaseline] = useState(
    rule.fallbackDenialMessage ?? DEFAULT_FALLBACK_DENIAL_MESSAGE,
  );
  const [savingDenial, setSavingDenial] = useState(false);
  const denialDirty = denialMessage.trim() !== savedBaseline.trim();

  const saveDenialMessage = async () => {
    setSavingDenial(true);
    try {
      const saved = await setRoutingFallbackMessage(denialMessage.trim());
      setSavedBaseline(saved || denialMessage.trim());
      showToast('Denial message updated for all rules.', 'success');
      onSaved?.();
    } catch (err: any) {
      showToast(err?.response?.data?.message || 'Failed to update denial message.', 'error');
    } finally {
      setSavingDenial(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[88vh] max-w-2xl flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="shrink-0 space-y-1.5 border-b border-border px-6 pb-4 pt-6 text-left">
          <div className="flex flex-wrap items-center gap-2">
            <DialogTitle>{rule.name}</DialogTitle>
            <span
              className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                rule.active
                  ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {rule.active ? 'ACTIVE' : 'INACTIVE'}
            </span>
          </div>
          <DialogDescription>
            {rule.merchantName ? `${rule.merchantName} · ` : ''}
            {rule.merchantTerminalName
              ? `Terminal: ${rule.merchantTerminalName} · `
              : 'All terminals · '}
            How a payment flows through this rule — first matching group wins (one attempt; a decline is
            final).
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col items-stretch overflow-x-hidden overflow-y-auto px-6 py-4">
          {/* Start */}
          <div className="flex justify-center">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary px-3 py-1 text-xs font-semibold text-foreground/80">
              <BsLightningChargeFill className="h-3 w-3 text-primary" /> Payment received
            </span>
          </div>
          <DownConnector label="check" />

          {/* Groups */}
          {groups.map((g, i) => (
            <Fragment key={i}>
              <div className="flex min-w-0 flex-col gap-2.5 sm:flex-row sm:items-stretch">
                {/* decision card */}
                <div className="min-w-0 flex-1 rounded-xl border-2 border-border bg-card p-3.5 shadow-sm">
                  <div className="mb-2 flex items-center gap-2">
                    <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary">
                      {i + 1}
                    </span>
                    <span className="text-xs font-semibold uppercase tracking-wide text-foreground/70">
                      {i === 0 ? 'If matches' : 'Else if matches'}
                    </span>
                  </div>
                  <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                    <Chip label={fmtOpList(g.countryCodes, g.countryOperator)} tone="country" />
                    <span className="text-[11px] text-foreground/60">and</span>
                    <Chip label={fmtOpList(g.currencies, g.currencyOperator)} tone="currency" />
                    {fmtList(g.userEmails) !== 'Any' ? (
                      <>
                        <span className="text-[11px] text-foreground/60">and</span>
                        <Chip label={`Email ${fmtOpList(g.userEmails, g.userEmailOperator)}`} tone="device" />
                      </>
                    ) : null}
                    {fmtList(g.deviceTypes) !== 'Any' ? (
                      <>
                        <span className="text-[11px] text-foreground/60">and</span>
                        <Chip label={fmtList(g.deviceTypes)} tone="device" />
                      </>
                    ) : null}
                    {g.riskRules && fmtList(g.riskRules) !== 'Any' ? (
                      <>
                        <span className="text-[11px] text-foreground/60">and</span>
                        <Chip label={`Risk ${fmtOpList(g.riskRules, g.riskRulesOperator)}`} tone="device" />
                      </>
                    ) : null}
                    <span className="text-[11px] text-foreground/60">and</span>
                    <Chip label={fmtRange(g)} tone="amount" />
                  </div>
                </div>

                {/* match → MID */}
                <div className="flex items-center gap-2 sm:w-[46%] sm:shrink-0">
                  <span className="hidden items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-emerald-600 dark:text-emerald-400 sm:inline-flex">
                    match <BsArrowRight className="h-3 w-3" />
                  </span>
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-emerald-600 dark:text-emerald-400 sm:hidden">
                    <BsArrowDown className="h-3 w-3" /> match
                  </span>
                  <div className="min-w-0 flex-1 rounded-xl border-2 border-emerald-500/40 bg-emerald-500/10 p-3">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700/80 dark:text-emerald-400/90">
                      Route to
                    </div>
                    <div className="break-words text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                      {g.targetMidLabel ?? '—'}
                    </div>
                  </div>
                </div>
              </div>
              <DownConnector label="no match" />
            </Fragment>
          ))}

          {/* Fallback / reject terminal */}
          {rule.fallbackMidLabel ? (
            <div className="rounded-xl border-2 border-dashed border-amber-500/50 bg-amber-500/10 p-3.5">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
                No group matched → global fallback
              </div>
              <div className="break-words text-sm font-semibold text-amber-800 dark:text-amber-300">
                {rule.fallbackMidLabel}
              </div>
            </div>
          ) : (
            <div className="rounded-xl border-2 border-dashed border-destructive/50 bg-destructive/10 p-3.5">
              <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-destructive">
                No group matched · no fallback — denial message
              </div>
              <p className="mb-2 text-[11px] text-muted-foreground">
                Shown to the shopper/merchant and recorded on the failed transaction when a routing
                rule rejects a payment. This message is <strong>global</strong> — it applies to every
                rule, so editing it here changes it everywhere. Leave blank to use the default.
              </p>
              <Textarea
                value={denialMessage}
                onChange={(e) => setDenialMessage(e.target.value)}
                rows={2}
                maxLength={2000}
                placeholder={DEFAULT_FALLBACK_DENIAL_MESSAGE}
                className="text-sm"
              />
              <div className="mt-2 flex justify-end">
                <Button
                  size="sm"
                  onClick={saveDenialMessage}
                  disabled={savingDenial || !denialDirty}
                >
                  {savingDenial ? 'Saving…' : 'Save message'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
